import { appConfig } from '../../config/env';
import { getFirebaseAuth } from '../../config/firebase';
import {
  ApiError,
  AuthenticationError,
  AuthorizationError,
  NetworkError,
  ServerError,
} from '../../types/errors';

/**
 * APIクライアント
 * Firebase認証トークン付きのHTTPリクエストを実行
 */
class ApiClient {
  /**
   * ベースURLを取得
   */
  private getBaseUrl(): string {
    return appConfig.api.baseUrl;
  }

  /**
   * Firebase認証トークンを取得
   *
   * `getIdToken()` は forceRefresh=false で呼ぶ（期限切れ時のみ Firebase 側で自動リフレッシュ）。
   * forceRefresh=true で毎回呼ぶと、HARC 系 10 種 + 他 API の並列発火で
   * `securetoken.googleapis.com` を短時間に大量に叩き `auth/quota-exceeded` を誘発するため。
   */
  private async getAuthToken(): Promise<string> {
    const auth = getFirebaseAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new AuthenticationError('ログインが必要です');
    }

    return user.getIdToken();
  }

  /**
   * GETリクエストを実行
   */
  async get<T>(path: string, params?: Record<string, string>): Promise<T> {
    const token = await this.getAuthToken();
    const baseUrl = this.getBaseUrl();

    let url = `${baseUrl}${path}`;
    if (params) {
      const searchParams = new URLSearchParams(params);
      url += `?${searchParams.toString()}`;
    }

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        await this.handleErrorResponse(response);
      }

      return response.json();
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new NetworkError('ネットワークエラーが発生しました');
    }
  }

  /**
   * POSTリクエストを実行
   * @param path - APIパス
   * @param body - リクエストボディ
   * @returns レスポンスボディ
   */
  async post<T>(path: string, body: unknown): Promise<T> {
    const token = await this.getAuthToken();
    const baseUrl = this.getBaseUrl();
    const url = `${baseUrl}${path}`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        await this.handleErrorResponse(response);
      }

      return response.json();
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new NetworkError('ネットワークエラーが発生しました');
    }
  }

  /**
   * POST リクエストを実行し、204 No Content も許容する。
   * 200 OK 時は JSON ボディを返し、204 No Content 時は null を返す。
   * @param path - APIパス
   * @param body - リクエストボディ
   * @returns レスポンスボディ、または 204 の場合は null
   */
  async postOptional<T>(path: string, body: unknown): Promise<T | null> {
    const token = await this.getAuthToken();
    const baseUrl = this.getBaseUrl();
    const url = `${baseUrl}${path}`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        await this.handleErrorResponse(response);
      }

      if (response.status === 204) {
        return null;
      }
      return response.json();
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new NetworkError('ネットワークエラーが発生しました');
    }
  }

  /**
   * PATCH リクエストを実行（部分更新）
   *
   * `undefined` のフィールドは `JSON.stringify` により自動的に除外される（= 変更しない）。
   * `null` は明示的に送信される（= クリア）。
   *
   * @param path - APIパス
   * @param body - リクエストボディ（部分更新の場合、変更したいフィールドだけを含める）
   * @returns レスポンスボディ
   */
  async patch<T>(path: string, body: unknown): Promise<T> {
    const token = await this.getAuthToken();
    const baseUrl = this.getBaseUrl();
    const url = `${baseUrl}${path}`;

    try {
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        await this.handleErrorResponse(response);
      }

      return response.json();
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new NetworkError('ネットワークエラーが発生しました');
    }
  }

  /**
   * DELETE リクエスト (body なし)。
   * @param path - エンドポイントのパス
   * @returns レスポンス JSON (200 OK 想定、 204 は呼び出し側で扱わない)
   */
  async delete<T>(path: string): Promise<T> {
    const token = await this.getAuthToken();
    const baseUrl = this.getBaseUrl();
    const url = `${baseUrl}${path}`;

    try {
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        await this.handleErrorResponse(response);
      }

      return response.json();
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new NetworkError('ネットワークエラーが発生しました');
    }
  }

  /**
   * エラーレスポンスを処理
   */
  private async handleErrorResponse(response: Response): Promise<never> {
    const status = response.status;

    switch (status) {
      case 401:
        throw new AuthenticationError('認証が必要です');
      case 403:
        throw new AuthorizationError('アクセス権限がありません');
      case 500:
        throw new ServerError('サーバーエラーが発生しました');
      default:
        throw new ApiError(`APIエラーが発生しました (${status})`, status);
    }
  }
}

/** APIクライアントのシングルトンインスタンス */
export const apiClient = new ApiClient();
